// Shared by NudgeDashboard.vue and NudgeAnalytics.vue.
//
// Both pages read the legacy `opportunities` schema, because that's where the always-on
// Create Campaign flow persists (see server/src/controllers/nudge-console/persistCampaign.js)
// and there's no dedicated Nudge Console campaign table. Every label below is a
// presentation-layer read of a legacy column — nothing here is stored.

// Matches OPPORTUNITY_STATUS in server/src/controllers/opportunityEngineManagement.js.
export const CAMPAIGN_STATUS = {
  draft: 0,
  active: 1,
  expired: 2,
  deleted: 3
}

export const STATUS_LABELS = {
  0: 'Draft',
  1: 'Active',
  2: 'Expired',
  3: 'Deleted'
}

// Modifier classes on `.nc-badge` from nudge-console-theme.css — tinted pills, not
// AdminLTE's saturated `label` fills.
export const STATUS_BADGE_CLASSES = {
  0: 'nc-badge-warning',
  1: 'nc-badge-success',
  2: 'nc-badge-neutral',
  3: 'nc-badge-danger'
}

// Dashboard tabs. `status` is what getOpportunityList's own filter takes; `all` passes no
// status at all, and `deleted` goes through that endpoint's separate isDeleted branch
// (deleted campaigns are status 3, but the endpoint only returns them via that flag).
export const DASHBOARD_TABS = [
  { key: 'all', label: 'All', status: null, isDeleted: false },
  { key: 'active', label: 'Active', status: CAMPAIGN_STATUS.active, isDeleted: false },
  { key: 'draft', label: 'Draft', status: CAMPAIGN_STATUS.draft, isDeleted: false },
  { key: 'expired', label: 'Expired', status: CAMPAIGN_STATUS.expired, isDeleted: false },
  { key: 'deleted', label: 'Deleted', status: null, isDeleted: true },
  // Not an opportunity status at all — wizard drafts live in ai_console_chat, so this tab
  // renders a different card and hits a different endpoint. PRD §3.3.3.
  { key: 'drafts', label: 'Draft Chats', status: null, isDeleted: false, isWizardDraft: true }
]

// getOpportunityList returns is_nudge_console as a computed boolean (see
// NUDGE_CONSOLE_CAMPAIGN_SQL in opportunityEngineManagement.js). It arrives as a real
// boolean over JSON, but coerce anyway rather than trust a driver's stringified `true`.
export function isNudgeConsoleCampaign (campaign) {
  return campaign.is_nudge_console === true || campaign.is_nudge_console === 'true'
}

// The one status change this page offers, per current status. Active campaigns get paused
// back to Draft; Draft and Expired ones get activated. Mirrors changeOpportunityStatusMap
// in ../opportunity-management/constants.js, but worded for nudges ("Pause", not "Draft").
export const STATUS_ACTION = {
  0: { label: 'Activate', to: CAMPAIGN_STATUS.active, buttonClass: 'nc-btn-primary' },
  1: { label: 'Pause', to: CAMPAIGN_STATUS.draft, buttonClass: 'nc-btn-ghost' },
  2: { label: 'Activate', to: CAMPAIGN_STATUS.active, buttonClass: 'nc-btn-primary' }
}

// Set by the template status watcher (server/src/notifications/campaignGate.js) when a
// campaign is held back by its WhatsApp template. Shown instead of the plain status, with
// the reason as a tooltip. Activating by hand clears it.
export const TEMPLATE_GATES = {
  AWAITING_TEMPLATE: {
    label: 'Awaiting template approval',
    badge: 'nc-badge-warning',
    action: 'Activate now',
    confirm: 'It goes live by itself once WhatsApp approves its template. Activating now skips that wait, and sends may fail until the template is approved.'
  },
  BLOCKED: {
    label: 'Paused: template issue',
    badge: 'nc-badge-danger',
    action: 'Resume',
    confirm: "Its template still isn't approved on WhatsApp, so sends will fail. Fix the template first unless you're sure."
  },
  READY_TO_RESUME: {
    label: 'Ready to resume',
    badge: 'nc-badge-teal',
    action: 'Resume',
    confirm: 'Its template is approved again. Resuming switches the campaign back on.'
  },
  WINDOW_MISSED: {
    label: 'Pick a new send time',
    badge: 'nc-badge-warning',
    action: 'Activate',
    confirm: 'Its scheduled send time passed while it waited for template approval. Edit the send time before activating, or it may not send.'
  }
}

// ── Dispatch type (PRD §3.3.2) ──────────────────────────────────────────────

// The PRD separates Active campaigns into Batch OOS and Triggered. Rather than two
// sections, the Dashboard carries it as a filter plus a per-card badge — the tabs already
// own the vertical split, and one grid keeps the page scannable.
//
// `lower_inactivity_timing` is the only column that tells them apart: set means the
// campaign fires per-user on a trigger condition, null means a fixed audience and a single
// send. Values the always-on flow writes are "HH:MM" (short window) or "d:<n>" (long) —
// see computeLowerInactivityTiming in persistCampaign.js.
export const CAMPAIGN_TYPE_OPTIONS = [
  { value: '', label: 'All types' },
  { value: 'triggered', label: 'Triggered' },
  { value: 'batch', label: 'One-time batch' }
]

export function isTriggeredCampaign (campaign) {
  return Boolean(campaign && campaign.lower_inactivity_timing)
}

// Label, icon and badge class in one place, so the filter option and the card badge can't
// describe the same campaign differently.
export function describeCampaignType (campaign) {
  return isTriggeredCampaign(campaign)
    ? { label: 'Triggered', icon: 'fa fa-refresh', className: 'nd-type-triggered' }
    : { label: 'One-time batch', icon: 'fa fa-paper-plane-o', className: 'nd-type-batch' }
}

// Reverses computeLowerInactivityTiming() in persistCampaign.js: short-window campaigns
// store "HH:MM", long-window ones store "d:<days>". Anything else is a legacy value this
// flow didn't write, so it's shown as-is rather than guessed at.
export function describeTrigger (campaign) {
  const timing = campaign.lower_inactivity_timing
  if (!timing) return 'No inactivity trigger'

  // createOrUpdateOpportunity.vue writes `${units.charAt(0)}:${quantity}` — so 'd:7' is
  // 7 days and 'h:6' is 6 hours. Both are real stored values; 'h:' used to fall through
  // and render as a raw "h:0" in the UI.
  const unitMatch = /^([dh]):(\d+)$/.exec(timing)
  if (unitMatch) {
    const amount = Number(unitMatch[2])
    const unit = unitMatch[1] === 'd' ? 'day' : 'hour'
    if (!amount) return `Immediately on inactivity`
    return `${amount} ${unit}${amount === 1 ? '' : 's'} of inactivity`
  }

  if (/^\d{1,2}:\d{2}$/.test(timing)) {
    const [hours, minutes] = timing.split(':').map(Number)
    const total = (hours * 60) + minutes
    if (!total) return 'Immediate'
    if (total < 60) return `${total} minutes of inactivity`
    return minutes
      ? `${hours}h ${minutes}m of inactivity`
      : `${hours} hour${hours === 1 ? '' : 's'} of inactivity`
  }

  return timing
}

// is_in_session is the only stored signal for short- vs long-window. A stage-reached
// trigger can't reach this schema at all (persistCampaign.js rejects it on Activate), so
// there's deliberately no stage case here.
export function describeSession (campaign) {
  return campaign.is_in_session
    ? 'Short-window · in-session'
    : 'Long-window · out-of-session'
}

// A campaign with more than one group is running an A/B test — the always-on flow writes
// Group 1 / Group 2 with a split_percent each (buildMessageGroups in persistCampaign.js).
export function describeSplit (campaign) {
  const groups = campaign.group_list || []
  if (groups.length < 2) return null
  return groups.map(g => `${g.split_percent || 0}%`).join(' / ')
}

export function safeInt (value) {
  const n = parseInt(value, 10)
  return Number.isNaN(n) ? 0 : n
}

// Every rate on the Analytics page is recomputed from the raw counts rather than read from
// a precomputed percent column, so the roll-up tiles and the per-row numbers can't disagree.
// One decimal place: these are small populations, and rounding to whole percent hides real
// movement between campaigns.
export function percentage (part, whole) {
  const total = safeInt(whole)
  if (!total) return 0
  return Math.round((safeInt(part) / total) * 1000) / 10
}

// ── Calendar-date helpers ───────────────────────────────────────────────────
//
// All of these used to go through `new Date(...).toISOString().slice(0, 10)`, which is wrong
// for any timezone that isn't UTC: it reads a LOCAL date and prints a UTC one. In IST
// (UTC+5:30) `addDays('2022-07-22', 1)` returned '2022-07-22' — local midnight the next day
// is still the previous day in UTC — so the trend chart's day loop never advanced, ran to
// its 400-iteration guard, and rendered 400 bars with an unreadable axis.
//
// The rule below: "which calendar day is it for this viewer" uses LOCAL getters, and date
// ARITHMETIC happens entirely in UTC, where every day is exactly 24 hours and no DST
// transition can swallow or duplicate one.

const pad = n => String(n).padStart(2, '0')

// A Date (or timestamp) -> the YYYY-MM-DD the viewer would call it.
export function isoOf (value) {
  if (!value) return ''
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// A server-supplied day, which may arrive as 'YYYY-MM-DD' or a full timestamp. A plain date
// string is taken verbatim rather than round-tripped through Date, which would shift it.
export function dayKey (value) {
  const text = String(value == null ? '' : value)
  return /^\d{4}-\d{2}-\d{2}/.test(text) ? text.slice(0, 10) : isoOf(text)
}

// 'YYYY-MM-DD' + n days, in UTC. Returns '' for anything that isn't a date string, so a bad
// input can't silently become "today".
export function addDays (iso, days) {
  const text = String(iso == null ? '' : iso).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return ''
  const [year, month, day] = text.split('-').map(Number)
  const d = new Date(Date.UTC(year, month - 1, day))
  d.setUTCDate(d.getUTCDate() + days)
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

// Whole days from `startIso` to `endIso`, inclusive of both ends. Negative when reversed.
export function daysBetween (startIso, endIso) {
  const start = String(startIso || '').slice(0, 10)
  const end = String(endIso || '').slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return 0
  const [sy, sm, sd] = start.split('-').map(Number)
  const [ey, em, ed] = end.split('-').map(Number)
  const ms = Date.UTC(ey, em - 1, ed) - Date.UTC(sy, sm - 1, sd)
  return Math.round(ms / 86400000) + 1
}

// Parses a calendar date as LOCAL midnight, for display. 'YYYY-MM-DD' passed straight to
// `new Date()` is parsed as UTC midnight, which renders as the previous day for any viewer
// behind UTC — an off-by-one on every axis label in the Americas.
export function localDate (iso) {
  const text = String(iso == null ? '' : iso).slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(text)) return null
  const [year, month, day] = text.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export function isoDaysAgo (days) {
  return addDays(todayIso(), -days)
}

export function todayIso () {
  return isoOf(new Date())
}

// ── Create Campaign wizard drafts (PRD §3.3.3) ──────────────────────────────

// The chat session's `purpose`, used both to create the session and to pick our own out of
// the user's session list. GET /ai-console-chat has no per-feature column, so every
// consumer of that endpoint must filter client-side on this string or risk resuming (or
// listing) a different feature's chat.
export const CHAT_PURPOSE = 'Nudge Console — Create Campaign'

// Maps the backend's step string (nudgeCampaignChat.js) to the 4-step progress bar used by
// trigger-based flows (always-on, in-session — no separate audience-build step, no send
// window); Batch OOS keeps the original 5-step numbering. Shared by ChatPanel.vue (to drive
// the steps bar) and the Dashboard's Drafts tab (to say which step a draft stopped on), so
// the two can't disagree about where a draft got to. Keep it in step with the step machine:
// a step missing here silently falls back to 2.
export const WIZARD_STEP_NUMBER = {
  start: 2,
  country: 2,
  customer: 2,
  journey: 2,
  channel: 2,
  language: 2,
  trigger_type: 2,
  trigger_stage: 2,
  trigger_window_kind: 2,
  trigger_window_value: 2,
  trigger_max_nudges: 2,
  trigger_add_more: 2,
  content_template: 3,
  content_goal: 3,
  content_tone: 3,
  content: 3,
  content_template_var_source: 3,
  content_template_var_values: 3,
  content_template_header: 3,
  content_template_media: 3,
  content_template_media_same: 3,
  content_template_media_filename: 3,
  content_template_create: 3,
  content_ab_offer: 3,
  content_ab_review: 3,
  ab_split: 3,
  content_destination: 3,
  content_destination_pick: 3,
  review: 4,
  done: 4
}

// Trigger-based flows run a 4-step bar (no Audience, no Send Window) — see
// CreateCampaign.vue's stepsLabels. Batch OOS's 5-step shape isn't built yet.
export const WIZARD_STEP_LABELS = ['Nudge Type', 'Trigger', 'Content', 'Review']

// ── AI Nudge Creation (standalone template chat, Templates PRD 3.3) ─────────────

export const TEMPLATE_CHAT_PURPOSE = 'Nudge Console — Create Template'

export const TEMPLATE_STEP_LABELS = ['Purpose', 'Where it lives', 'Content', 'Name & checks', 'Submit']

// templateChat.js's steps -> the 5-step bar. A step missing here falls back to 1.
export const TEMPLATE_STEP_NUMBER = {
  start: 1,
  customers: 2,
  countries: 2,
  journey: 2,
  languages: 2,
  add_source: 2,
  add_targets: 2,
  goal: 3,
  tone: 3,
  personalise: 3,
  cta: 3,
  quick_replies: 3,
  content: 3,
  var_source: 3,
  var_samples: 3,
  header: 3,
  media: 3,
  media_same: 3,
  media_filename: 3,
  add_samples: 3,
  name: 4,
  checks: 4,
  confirm: 4,
  add_checks: 4,
  review: 5,
  add_review: 5,
  submitted: 5,
  done: 5
}

// A saved chat session is a template draft when its summary says so (TemplateChatPanel.vue).
export function isTemplateDraft (summary) {
  return !!summary && summary.mode === 'template'
}

// "Step 3 of 4 · Content", from a saved chat session's summary. A session that never got
// past the opening nudge-type question has no context.step at all.
export function describeDraftStep (summary) {
  const context = (summary && summary.context) || {}
  if (isTemplateDraft(summary)) {
    const number = TEMPLATE_STEP_NUMBER[context.step] || 1
    return `Step ${number} of ${TEMPLATE_STEP_LABELS.length} · ${TEMPLATE_STEP_LABELS[number - 1]}`
  }
  const nudgeType = summary && summary.nudgeType
  if (!nudgeType) return 'Step 1 of 4 · Nudge Type'
  const number = WIZARD_STEP_NUMBER[context.step] || 2
  return `Step ${number} of ${WIZARD_STEP_LABELS.length} · ${WIZARD_STEP_LABELS[number - 1] || 'Trigger'}`
}

// Best-effort name for a draft that has no campaign name yet — the always-on flow only
// generates one at the review step (generateCampaignName in nudgeCampaignChat.js).
export function describeDraftName (summary) {
  const context = (summary && summary.context) || {}
  if (isTemplateDraft(summary)) {
    const s = context.summary || {}
    return s.name || [s.purpose ? `${s.purpose} template` : 'New template', (s.countries || []).join(', ')].filter(Boolean).join(' · ')
  }
  if (context.campaignName) return context.campaignName
  const parts = [context.customer, context.country, context.journey && context.journey.name].filter(Boolean)
  return parts.length ? parts.join(' - ') : 'Untitled campaign'
}



// WEBPACK FOOTER //
// ./src/components/nudge-console/constants.js